import { useCallback, useEffect } from 'react';
import { req, useApiGet, useApiLoading } from 'react-reqq-lite';
import { useMerchantSelected } from 'core-modules/merchants/hooks';
import * as actions from './actions';
import * as c from './constants';
import {
  transformBusinessCorporation,
  transformBusinessGovernment,
  transformBusinessIndividual,
  transformBusinessSole,
} from './transformers';

export const useFormGeneralInfo = (cb) => {
  const [, data] = useMerchantSelected();
  const fetch = useCallback(() => {
    if (!data?.id) return;
    actions.promiseGetGeneralInfo(data.id).then(cb);
  }, [data, cb]);
  useEffect(() => {
    fetch();
  }, [fetch]);
  return [fetch];
};

export const useGeneralInfo = () => {
  const isLoading = useApiLoading(c.GENERAL_INFO, 'get');
  const data = useApiGet(c.GENERAL_INFO, {});
  return [isLoading, data];
};

export const useSaveGeneralInfo = () => {
  const isLoading = useApiLoading(c.GENERAL_INFO, 'put');
  const [, data] = useMerchantSelected();
  const submit = useCallback(
    (payload, onSuccess, onError) => {
      actions.saveGeneralInfo(data.id, payload, onSuccess, onError);
    },
    [data]
  );
  return [isLoading, submit];
};

export const useCheckSavedBusinessInfo = () => {
  const isLoading = useApiLoading(c.CHECK_SAVED_BUSINESS_INFO, 'get');
  const data = useApiGet(c.CHECK_SAVED_BUSINESS_INFO, {});
  const [, biller] = useMerchantSelected();
  useEffect(() => {
    if (biller?.id) {
      actions.checkSavedBusinessInfo(biller?.id);
    }
  }, [biller]);
  return [isLoading, data];
};

export const useFormBusinessCorporation = (cb) => {
  const [, data] = useMerchantSelected();
  const [, checkSavedBusinessInfo] = useCheckSavedBusinessInfo();
  const fetch = useCallback(() => {
    if (!checkSavedBusinessInfo?.CORPORATION) {
      req.set(c.BUSINESS_INFO, transformBusinessCorporation({}));
      cb(transformBusinessCorporation({}));
      return;
    }
    actions.promiseGetBusinessCorporation(data.id).then(cb);
  }, [data, cb, checkSavedBusinessInfo]);
  useEffect(() => {
    fetch();
  }, [fetch]);
  return [fetch];
};

export const useBusinessCorporation = () => {
  const isLoading = useApiLoading(c.BUSINESS_INFO, 'get');
  const data = useApiGet(c.BUSINESS_INFO, {});
  return [isLoading, data];
};

export const useSaveBusinessCorporation = () => {
  const isLoading = useApiLoading(c.BUSINESS_INFO, 'put');
  const [, data] = useMerchantSelected();
  const submit = useCallback(
    (payload, onSuccess, onError) => {
      actions.saveBusinessCorporation(data.id, payload, onSuccess, onError);
    },
    [data]
  );
  return [isLoading, submit];
};

export const useFormBusinessSole = (cb) => {
  const [, data] = useMerchantSelected();
  const [, checkSavedBusinessInfo] = useCheckSavedBusinessInfo();
  const fetch = useCallback(() => {
    if (!checkSavedBusinessInfo?.SOLE_PROPRIETORSHIP) {
      req.set(c.BUSINESS_INFO, transformBusinessSole({}));
      cb(transformBusinessSole({}));
      return;
    }
    actions.promiseGetBusinessSole(data.id).then(cb);
  }, [data, cb, checkSavedBusinessInfo]);
  useEffect(() => {
    fetch();
  }, [fetch]);
  return [fetch];
};

export const useBusinessSole = () => {
  const isLoading = useApiLoading(c.BUSINESS_INFO, 'get');
  const data = useApiGet(c.BUSINESS_INFO, {});
  return [isLoading, data];
};

export const useSaveBusinessSole = () => {
  const isLoading = useApiLoading(c.BUSINESS_INFO, 'put');
  const [, data] = useMerchantSelected();
  const submit = useCallback(
    (payload, onSuccess, onError) => {
      actions.saveBusinessSole(data.id, payload, onSuccess, onError);
    },
    [data]
  );
  return [isLoading, submit];
};

export const useFormBusinessIndividual = (cb) => {
  const [, data] = useMerchantSelected();
  const [, checkSavedBusinessInfo] = useCheckSavedBusinessInfo();
  const fetch = useCallback(() => {
    if (!checkSavedBusinessInfo?.INDIVIDUAL) {
      req.set(c.BUSINESS_INFO, transformBusinessIndividual({}));
      cb(transformBusinessIndividual({}));
      return;
    }
    actions.promiseGetBusinessIndividual(data.id).then(cb);
  }, [data, cb, checkSavedBusinessInfo]);
  useEffect(() => {
    fetch();
  }, [fetch]);
  return [fetch];
};

export const useBusinessIndividual = () => {
  const isLoading = useApiLoading(c.BUSINESS_INFO, 'get');
  const data = useApiGet(c.BUSINESS_INFO, {});
  return [isLoading, data];
};

export const useSaveBusinessIndividual = () => {
  const isLoading = useApiLoading(c.BUSINESS_INFO, 'put');
  const [, data] = useMerchantSelected();
  const submit = useCallback(
    (payload, onSuccess, onError) => {
      actions.saveBusinessIndividual(data.id, payload, onSuccess, onError);
    },
    [data]
  );
  return [isLoading, submit];
};

export const useFormBusinessPartner = (cb) => {
  const [, data] = useMerchantSelected();
  const fetch = useCallback(() => {
    actions.promiseGetBusinessPartner(data.id).then(cb);
  }, [data, cb]);
  useEffect(() => {
    fetch();
  }, [fetch]);
  return [fetch];
};

export const useBusinessPartner = () => {
  const isLoading = useApiLoading(c.BUSINESS_INFO, 'get');
  const data = useApiGet(c.BUSINESS_INFO, {});
  return [isLoading, data];
};

export const useSaveBusinessPartner = () => {
  const isLoading = useApiLoading(c.BUSINESS_INFO, 'put');
  const [, data] = useMerchantSelected();
  const submit = useCallback(
    (payload, onSuccess, onError) => {
      actions.saveBusinessPartner(data.id, payload, onSuccess, onError);
    },
    [data]
  );
  return [isLoading, submit];
};

export const useFormBusinessGovernment = (cb) => {
  const [, data] = useMerchantSelected();
  const [, checkSavedBusinessInfo] = useCheckSavedBusinessInfo();
  const fetch = useCallback(() => {
    if (!checkSavedBusinessInfo?.GOVERNMENT) {
      req.set(c.BUSINESS_INFO, transformBusinessGovernment({}));
      cb(transformBusinessGovernment({}));
      return;
    }
    actions.getBusinessGovernment(data.id, cb);
  }, [data, cb, checkSavedBusinessInfo]);
  useEffect(() => {
    fetch();
  }, [fetch]);
  return [fetch];
};

export const useBusinessGovernment = () => {
  const isLoading = useApiLoading(c.BUSINESS_INFO, 'get');
  const data = useApiGet(c.BUSINESS_INFO, {});
  return [isLoading, data];
};

export const useSaveBusinessGovernment = () => {
  const isLoading = useApiLoading(c.BUSINESS_INFO, 'put');
  const [, data] = useMerchantSelected();
  const submit = useCallback(
    (payload, onSuccess, onError) => {
      actions.saveBusinessGovernment(data.id, payload, onSuccess, onError);
    },
    [data]
  );
  return [isLoading, submit];
};

export const useSubmitApplication = () => {
  const isLoading = useApiLoading(c.APPLICATION, 'put');
  const [, data] = useMerchantSelected();
  const submit = useCallback(
    (payload, onSuccess, onError) => {
      actions.submitApplication(data.id, payload, onSuccess, onError);
    },
    [data]
  );
  return [isLoading, submit];
};
